import type { DocumentDTO } from '@eriko/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ENDPOINTS } from '../api/endpoints';
import { apiClient } from '../lib/api';

export function useOrgDocuments(orgId: string | null) {
  return useQuery({
    queryKey: ['org-documents', orgId],
    queryFn: async () => {
      const res = await apiClient.get<DocumentDTO[]>(ENDPOINTS.documents.list, {
        params: { orgId },
      });
      return res.data;
    },
    enabled: Boolean(orgId),
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
  });
}

export function useAdminDocMutations(orgId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-documents', orgId] });

  const retry = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<{ conversationId: string }>(
        ENDPOINTS.documents.retry(id),
        {},
        { params: { orgId } },
      );
      return res.data;
    },
    onSuccess: invalidate,
  });

  const markFailed = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.patch<DocumentDTO>(
        ENDPOINTS.documents.detail(id),
        { status: 'FAILED' },
        { params: { orgId } },
      );
      return res.data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete<{ id: string }>(ENDPOINTS.documents.detail(id), {
        params: { orgId },
      });
      return res.data;
    },
    onSuccess: invalidate,
  });

  return { retry, markFailed, remove };
}
